import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function CancellationRefundPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-tools-fill text-white text-xl"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">Taskbudy</span>
            </Link>
            <Link
              to="/"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              <i className="ri-home-line text-xl"></i>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white">
          {/* Title Section */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Cancellation &amp; Refund Policy
            </h1>
            <p className="text-sm text-gray-500">
              Last updated: 15 January 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 leading-relaxed">
              This Cancellation &amp; Refund Policy explains how cancellations, refunds, and related charges are handled on the Taskbudy platform. This policy applies to both Customers and Providers and forms part of the Taskbudy Terms &amp; Conditions.
            </p>
          </div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. GENERAL PRINCIPLES
              </h2>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>1.1</strong> Taskbudy aims to treat Customers and Providers fairly while protecting the integrity of the platform.
                </p>
                <p>
                  <strong>1.2</strong> Refunds are not automatic and depend on the timing of the cancellation and the status of the job.
                </p>
                <p>
                  <strong>1.3</strong> Taskbudy reserves the right to make final decisions on cancellations and refunds where disputes arise, subject to applicable law.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. CUSTOMER CANCELLATIONS
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    2.1 Cancellation before a job is accepted
                  </h3>
                  <p className="text-gray-700">
                    If a Customer cancels a job before any Provider accepts it, the cancellation is free of charge and the Customer will receive a full refund if payment has been made.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    2.2 Cancellation after a job is accepted but before work starts
                  </h3>
                  <p className="text-gray-700 mb-2">
                    If a Customer cancels after a Provider has accepted the job but before work has started:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>A full or partial refund may be issued</li>
                    <li>Taskbudy may deduct a service or processing fee where applicable</li>
                    <li>Refund eligibility depends on timing and circumstances</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    2.3 Cancellation after work has started or Provider is on site
                  </h3>
                  <p className="text-gray-700 mb-2">
                    If a Customer cancels after work has started or the Provider has arrived on site:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Refunds are not guaranteed</li>
                    <li>Partial refunds may be issued at Taskbudy's discretion</li>
                    <li>Consideration will be given to work completed and costs incurred</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. PROVIDER CANCELLATIONS &amp; NO-SHOWS
              </h2>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>3.1</strong> Providers should only accept jobs they intend to complete.
                </p>
                <p>
                  <strong>3.2</strong> If a Provider cancels a job after accepting it, the cancellation will be recorded on the Provider's account.
                </p>
                <p>
                  <strong>3.3</strong> Repeated cancellations, late cancellations, or failure to attend jobs ("no-shows") may result in:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Warnings</li>
                  <li>Temporary suspension</li>
                  <li>Permanent removal from the platform</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. REFUNDS &amp; EARNINGS ADJUSTMENTS
              </h2>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>4.1</strong> If a refund is approved, any related Provider earnings may be reversed or adjusted.
                </p>
                <p>
                  <strong>4.2</strong> Commission will only be reversed where a job is fully refunded.
                </p>
                <p>
                  <strong>4.3</strong> If a Provider has already withdrawn earnings that are later refunded, Taskbudy may:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Deduct the amount from future earnings, or</li>
                  <li>Request repayment where legally permitted.</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Footer Note */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              For questions about cancellations or refunds, please contact Taskbudy support via the platform.
            </p>
            <p className="text-sm text-gray-900 font-semibold text-center mt-4">
              Taskbudy Ltd.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
